import type * as React from "react";
import { Tabs as RadixTabs } from "radix-ui";
import { cn } from "./cn";
import { useScrollHint } from "./useScrollHint";

// Tabs — a thin styled wrapper over Radix's Tabs primitive. Second real @ozpanel/ui
// component after Button; needed the moment a screen (Backups: status/targets/schedules/
// snapshots) has more than one sub-view — V6.7e's own menu map already calls for tabs on
// Mail and Databases too, so this is built once here rather than per-screen.

export const Tabs = RadixTabs.Root;

export function TabsList({ className, style, ...props }: React.ComponentProps<typeof RadixTabs.List>) {
  // Scrolls sideways rather than overflowing the page: five tabs do not fit across 320px. The hint
  // says when a tab is off screen (useScrollHint).
  const hint = useScrollHint<HTMLDivElement>('[data-state="active"]');
  return (
    <RadixTabs.List
      ref={hint.ref}
      className={cn("no-scrollbar mb-4 flex gap-1 overflow-x-auto border-b border-line", className)}
      style={{ ...style, ...hint.style }}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: React.ComponentProps<typeof RadixTabs.Trigger>) {
  return (
    <RadixTabs.Trigger
      className={cn(
        "shrink-0 whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted pointer-coarse:min-h-11",
        "hover:text-fg ",
        "data-[state=active]:border-[var(--ozp-accent)] data-[state=active]:text-fg dark:data-[state=active]:text-neutral-100",
        className,
      )}
      {...props}
    />
  );
}

export const TabsContent = RadixTabs.Content;
